'use client';

import { type EmblaCarouselType } from 'embla-carousel';
import { useState } from 'react';

import { Button } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { HeaderLevel2 } from '@/app/components/headers/header-level-2';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Image } from '@/app/components/media/image';
import { Sliders } from '@/app/components/sliders';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import styles from '@/app/cv/components/sections/products/index.module.css';
import type { ProductIESCSectionProps } from '@/app/cv/components/sections/products/types';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import backgroundColorStyles from '@/app/styles/colors/background-colors.module.css';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

export const ProductIESC = ({
  lead,
  title,
  description,
  buttons = [],
  items = [],
  testId = 'product-iesc',
}: ProductIESCSectionProps) => {
  useRevealAnim({ defaultBaseDelay: 0.04 });
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
  const { selectedIndex } = usePaginationSlider(emblaApi);

  const buttonContent = buttons.map((button) => ({
    ...button,
    className: cn('hidden lg:inline-flex', button.className),
  }));

  return (
    <Section
      id="iesc"
      size="lg"
      className={backgroundColorStyles['bg-gradient-dark-grey']}
      data-revealanim-group
      {...createDataAttributes({ test: `${testId}-section` })}
    >
      <Container
        className={cn('mb-6 text-white sm:mb-10', {
          'flex flex-col gap-y-6 lg:flex-row lg:gap-x-12': !!description,
        })}
        data-revealanim
        {...createDataAttributes({ test: `${testId}-container` })}
      >
        <HeaderLevel2
          theme="dark"
          lead={lead || ''}
          title={title || ''}
          leadClassName="text-white"
          {...(description
            ? {
                content1: {
                  content: description,
                  buttons: buttonContent,
                },
              }
            : {
                buttons: buttonContent,
              })}
        />
      </Container>

      <Container
        data-revealanim
        {...createDataAttributes({ test: `${testId}-slider-container` })}
      >
        <Sliders
          align="center"
          loop={false}
          hidden={false}
          onEmblaInit={setEmblaApi}
          containerClassName="-ml-4 sm:-ml-6 lg:ml-0 lg:gap-x-6"
          emblaOptions={{
            containScroll: 'trimSnaps',
            breakpoints: {
              ...(items.length <= 3 && {
                '(min-width: 1024px)': { active: false },
              }),
              ...(items.length === 4 && {
                '(min-width: 1280px)': { active: false },
              }),
            },
          }}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className={cn('shrink-0', styles.carouselSlide, styles.iescSlide)}
              {...createDataAttributes({
                test: `slide-${index}`,
                active: index === selectedIndex ? true : undefined,
              })}
            >
              {!!item.image && (
                <div
                  className="rounded-base-md overflow-hidden"
                  {...createDataAttributes({ test: 'slide-image' })}
                >
                  <Image
                    src={item.image}
                    alt={item.title || ''}
                    width={320}
                    height={180}
                    aspectRatio="aspect-16-9"
                    hoverEffect="zoom"
                    sizes="(min-width: 1024px) 25vw, 90vw"
                  />
                </div>
              )}
              <h3
                className="text-sub-h2 font-noto-bold where-span:text-inline-wrap mt-6 text-white"
                dangerouslySetInnerHTML={{ __html: item.title || '' }}
                {...createDataAttributes({ test: 'slide-title' })}
              />
              {!!item.description && (
                <div
                  className="text-body-1 mt-2 text-white"
                  dangerouslySetInnerHTML={{ __html: item.description }}
                  {...createDataAttributes({ test: 'slide-description' })}
                />
              )}
            </div>
          ))}
        </Sliders>
      </Container>

      {buttons.length > 0 && (
        <Container className="mt-6 sm:mt-10 lg:hidden" data-revealanim>
          <ButtonGroup>
            {buttons.map((button, index) => (
              <Button
                key={index}
                {...button}
                className={cn('w-full md:w-auto', button.className)}
              />
            ))}
          </ButtonGroup>
        </Container>
      )}
    </Section>
  );
};
